import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { showToast } from '../../utill/utill';

const IconStar = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
            d="M5 0.5L6.2 3.6L9.5 3.6L6.85 5.5L7.85 8.6L5 6.7L2.15 8.6L3.15 5.5L0.5 3.6L3.8 3.6L5 0.5Z"
            fill="var(--fg-anti-red, #ff1500)"
        />
    </svg>
);

const IconEye = ({ open }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {open ? (
            <>
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
            </>
        ) : (
            <>
                <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                <line x1="1" y1="1" x2="23" y2="23" />
            </>
        )}
    </svg>
);

const IconCheckLarge = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <polyline
            points="20 6 9 17 4 12"
            stroke="#ffffff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

export default function ForgotPassword() {
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSendingCode, setIsSendingCode] = useState(false);

    const [verificationCode, setVerificationCode] = useState('');
    const [verificationToken, setVerificationToken] = useState('');
    const [verifyError, setVerifyError] = useState('');
    const [isVerifying, setIsVerifying] = useState(false);
    const [timer, setTimer] = useState(180);
    const [isTimerActive, setIsTimerActive] = useState(false);
    const [canResend, setCanResend] = useState(false);

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [isResetting, setIsResetting] = useState(false);

    useEffect(() => {
        let interval = null;
        if (isTimerActive && timer > 0) {
            interval = setInterval(() => {
                setTimer(prev => {
                    if (prev <= 1) {
                        setIsTimerActive(false);
                        setCanResend(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isTimerActive, timer]);

    const formatTimer = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const handleSendCode = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setEmailError('이메일을 입력해주세요.');
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError('올바른 이메일 형식이 아닙니다.');
            return;
        }

        setIsSendingCode(true);
        setEmailError('');

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/email/send-code`, {
                email,
            });
            if (response.data?.verification_token) {
                setVerificationToken(response.data.verification_token);
            }
            setStep('verify');
            setIsTimerActive(true);
            setTimer(180);
            setCanResend(false);
            setVerificationCode('');
            showToast('인증 코드가 발송되었습니다.', 'success');
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.detail
                || '인증 코드 발송에 실패했습니다.';
            setEmailError(message);
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleResendCode = async () => {
        if (!canResend || isSendingCode) return;
        setIsSendingCode(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/user/email/send-code`, {
                email,
            });
            if (response.data?.verification_token) {
                setVerificationToken(response.data.verification_token);
            }
            setVerificationCode('');
            setVerifyError('');
            setIsTimerActive(true);
            setTimer(180);
            setCanResend(false);
            showToast('인증 코드가 재발송되었습니다.', 'success');
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.detail
                || '인증 코드 재발송에 실패했습니다.';
            showToast(message, 'error');
            setCanResend(true);
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleVerifyCode = async (e) => {
        e.preventDefault();
        if (!verificationCode.trim()) {
            setVerifyError('인증번호를 입력해주세요.');
            return;
        }
        if (verificationCode.length !== 6) {
            setVerifyError('인증 코드 6자리를 입력해주세요.');
            return;
        }

        setIsVerifying(true);
        setVerifyError('');

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/user/email/verify-code`, {
                email,
                code: verificationCode,
                verification_token: verificationToken,
            });
            setStep('reset');
            setIsTimerActive(false);
            showToast('인증이 완료되었습니다.', 'success');
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.detail
                || '인증번호가 일치하지 않습니다.';
            setVerifyError(message);
        } finally {
            setIsVerifying(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword.length < 8) {
            setPasswordError('비밀번호는 8자 이상이어야 합니다.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setPasswordError('비밀번호가 맞지 않습니다.');
            return;
        }

        setIsResetting(true);
        setPasswordError('');

        try {
            // 백엔드 연동 예정 — 신규 디자인 단계에서는 UI 흐름만 검증
            // await axios.post(`${process.env.REACT_APP_API_URL}/user/password/reset`, {
            //     email,
            //     new_password: newPassword,
            //     verification_token: verificationToken,
            // });
            setStep('done');
            showToast('비밀번호가 성공적으로 변경되었습니다.', 'success');
        } catch (error) {
            const message = error.response?.data?.detail
                || error.response?.data?.message
                || '비밀번호 변경에 실패했습니다.';
            setPasswordError(message);
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>비밀번호 찾기 - GrowFit</title>
                <meta name="description" content="GrowFit 계정 비밀번호를 찾고 재설정합니다. 이메일 인증을 통해 안전하게 비밀번호를 변경하세요." />
                <link rel="canonical" href="https://growfit.kr/forgot-password" />
                <meta property="og:title" content="비밀번호 찾기 - GrowFit" />
                <meta property="og:description" content="GrowFit 계정 비밀번호 재설정." />
                <meta property="og:url" content="https://growfit.kr/forgot-password" />
                <meta property="og:type" content="website" />
            </Helmet>

            <div className="forgot2026">
                <main className="forgot2026__main">
                    <div className="forgot2026__brand">
                        <img
                            className="forgot2026__brand-logo"
                            src="/images/growfit-star.png"
                            alt="GrowFit"
                        />
                        <span className="forgot2026__brand-text">GrowFit</span>
                    </div>

                    {step === 'email' && (
                        <form className="forgot2026__form" onSubmit={handleSendCode} noValidate>
                            <section className="forgot2026__section">
                                <h1 className="forgot2026__title">비밀번호 변경</h1>
                                <p className="forgot2026__desc">
                                    비밀번호를 변경하려면 계정에 연결된 이메일 또는 전화번호 입력해 주세요.
                                </p>

                                <div className="forgot2026__field">
                                    <label className="forgot2026__field-label" htmlFor="forgot-email">
                                        <span>이메일 또는 전화번호</span>
                                        <IconStar />
                                    </label>
                                    <input
                                        id="forgot-email"
                                        type="email"
                                        autoComplete="email"
                                        className={`forgot2026__input ${emailError ? 'forgot2026__input--error' : ''}`}
                                        placeholder="example@growfit.kr"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (emailError) setEmailError('');
                                        }}
                                        disabled={isSendingCode}
                                        required
                                    />
                                    {emailError && (
                                        <p className="forgot2026__field-error">{emailError}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="forgot2026__submit"
                                    disabled={isSendingCode}
                                >
                                    {isSendingCode ? '발송 중...' : '다음'}
                                </button>
                            </section>

                            <p className="forgot2026__bottom-link">
                                <span>비밀번호가 기억나셨나요?</span>
                                <Link to="/login">로그인</Link>
                            </p>
                        </form>
                    )}

                    {step === 'verify' && (
                        <form className="forgot2026__form" onSubmit={handleVerifyCode} noValidate>
                            <section className="forgot2026__section">
                                <h1 className="forgot2026__title">본인 인증</h1>
                                <p className="forgot2026__desc">
                                    이메일 또는 휴대전화로 수신한 인증코드를 입력하세요.
                                </p>

                                <div className="forgot2026__field">
                                    <label className="forgot2026__field-label" htmlFor="forgot-code">
                                        <span>인증코드</span>
                                        <IconStar />
                                    </label>
                                    <input
                                        id="forgot-code"
                                        type="text"
                                        inputMode="numeric"
                                        className={`forgot2026__input ${verifyError ? 'forgot2026__input--error' : ''}`}
                                        placeholder="인증번호 6자리"
                                        maxLength={6}
                                        value={verificationCode}
                                        onChange={(e) => {
                                            setVerificationCode(e.target.value.replace(/\D/g, ''));
                                            if (verifyError) setVerifyError('');
                                        }}
                                        disabled={isVerifying}
                                    />
                                    {verifyError && (
                                        <p className="forgot2026__field-error">{verifyError}</p>
                                    )}
                                    {!verifyError && isTimerActive && timer > 0 && (
                                        <p className="forgot2026__field-hint">남은 시간: {formatTimer(timer)}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="forgot2026__submit"
                                    disabled={isVerifying}
                                >
                                    {isVerifying ? '인증 중...' : '인증 완료'}
                                </button>
                            </section>

                            <p className="forgot2026__bottom-link">
                                <span>인증코드를 못 받으셨나요?</span>
                                <button
                                    type="button"
                                    className={`forgot2026__link-btn ${!canResend ? 'forgot2026__link-btn--disabled' : ''}`}
                                    onClick={handleResendCode}
                                    disabled={!canResend || isSendingCode}
                                >
                                    재전송
                                </button>
                            </p>

                            <p className="forgot2026__bottom-link">
                                <span>비밀번호가 기억나셨나요?</span>
                                <Link to="/login">로그인</Link>
                            </p>
                        </form>
                    )}

                    {step === 'reset' && (
                        <form className="forgot2026__form" onSubmit={handleResetPassword} noValidate>
                            <section className="forgot2026__section">
                                <h1 className="forgot2026__title">비밀번호 재설정</h1>
                                <p className="forgot2026__desc">비밀번호를 재설정해 주세요.</p>

                                <div className="forgot2026__field">
                                    <label className="forgot2026__field-label" htmlFor="forgot-new-pw">
                                        <span>재설정할 비밀번호</span>
                                        <IconStar />
                                    </label>
                                    <div className="forgot2026__pw-wrap">
                                        <input
                                            id="forgot-new-pw"
                                            type={showNewPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            className={`forgot2026__input forgot2026__input--has-toggle ${passwordError ? 'forgot2026__input--error' : ''}`}
                                            placeholder="새 비밀번호를 입력하세요"
                                            value={newPassword}
                                            onChange={(e) => {
                                                setNewPassword(e.target.value);
                                                if (passwordError) setPasswordError('');
                                            }}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="forgot2026__pw-toggle"
                                            onClick={() => setShowNewPassword(v => !v)}
                                            aria-label={showNewPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                        >
                                            <IconEye open={showNewPassword} />
                                        </button>
                                    </div>
                                </div>

                                <div className="forgot2026__field">
                                    <label className="forgot2026__field-label" htmlFor="forgot-confirm-pw">
                                        <span>재설정할 비밀번호 재입력</span>
                                        <IconStar />
                                    </label>
                                    <div className="forgot2026__pw-wrap">
                                        <input
                                            id="forgot-confirm-pw"
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            autoComplete="new-password"
                                            className={`forgot2026__input forgot2026__input--has-toggle ${passwordError ? 'forgot2026__input--error' : ''}`}
                                            placeholder="새 비밀번호를 재입력하세요"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                if (passwordError) setPasswordError('');
                                            }}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="forgot2026__pw-toggle"
                                            onClick={() => setShowConfirmPassword(v => !v)}
                                            aria-label={showConfirmPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                                        >
                                            <IconEye open={showConfirmPassword} />
                                        </button>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="forgot2026__submit"
                                    disabled={isResetting}
                                >
                                    {isResetting ? '변경 중...' : '변경 완료'}
                                </button>

                                {passwordError && (
                                    <p className="forgot2026__form-error">{passwordError}</p>
                                )}
                            </section>
                        </form>
                    )}

                    {step === 'done' && (
                        <section className="forgot2026__section forgot2026__section--done">
                            <div className="forgot2026__success-circle" aria-hidden="true">
                                <IconCheckLarge />
                            </div>
                            <h1 className="forgot2026__title">비밀번호 변경 완료</h1>
                            <p className="forgot2026__desc">
                                새 비밀번호로 성공적으로 변경되었습니다.<br />
                                변경된 비밀번호로 로그인해주세요.
                            </p>
                            <Link to="/login" className="forgot2026__submit forgot2026__submit--link">
                                로그인하러 가기
                            </Link>
                        </section>
                    )}
                </main>
            </div>
        </>
    );
}
